/**
 * Offline-First Roadmap Service
 * 
 * Provides roadmap data access using the offline-first architecture.
 * Falls back to IndexedDB when offline and syncs with Supabase when online.
 */

import { 
  roadmapMacroOps, 
  roadmapMesoOps, 
  type RoadmapMacroOffline, 
  type RoadmapMesoOffline 
} from '@/services/offline';
import { 
  fetchRoadmapMacroCacheFirst, 
  fetchRoadmapMesoCacheFirst,
  type CacheStrategyOptions 
} from '@/contexts/offline';

/**
 * Fetch macro curriculum with offline-first strategy
 */
export async function fetchMacroCurriculumOffline(
  options: CacheStrategyOptions = {}
): Promise<RoadmapMacroOffline[]> {
  try {
    const result = await fetchRoadmapMacroCacheFirst(options);
    return result.data || [];
  } catch (error) {
    console.warn('Failed to fetch macro curriculum, falling back to cache:', error);
    // Fallback to direct IndexedDB access
    return await roadmapMacroOps.getAllOrdered();
  }
}

/**
 * Fetch meso curriculum with offline-first strategy
 */
export async function fetchMesoCurriculumOffline(
  options: CacheStrategyOptions = {}
): Promise<RoadmapMesoOffline[]> {
  try {
    const result = await fetchRoadmapMesoCacheFirst(options);
    return result.data || [];
  } catch (error) {
    console.warn('Failed to fetch meso curriculum, falling back to cache:', error);
    // Fallback to direct IndexedDB access
    return await roadmapMesoOps.getAllOrdered();
  }
}

/**
 * Get macro curriculum by belt color
 */
export async function getMacroCurriculumByBelt(
  beltColor: string
): Promise<RoadmapMacroOffline[]> {
  try {
    return await roadmapMacroOps.getByBeltColor(beltColor);
  } catch (error) {
    console.error('Failed to get macro curriculum by belt:', error);
    return [];
  }
}

/**
 * Get meso curriculum by belt color
 */
export async function getMesoCurriculumByBelt(
  beltColor: string
): Promise<RoadmapMesoOffline[]> {
  try {
    return await roadmapMesoOps.getByBeltColor(beltColor);
  } catch (error) {
    console.error('Failed to get meso curriculum by belt:', error);
    return [];
  }
}

/**
 * Get curriculum by belt and stripe
 */
export async function getCurriculumByBeltAndStripe(
  beltColor: string,
  stripeCount: number
): Promise<{
  macro: RoadmapMacroOffline[];
  meso: RoadmapMesoOffline[];
}> {
  try {
    const [macro, meso] = await Promise.all([
      roadmapMacroOps.getByBeltAndStripe(beltColor, stripeCount),
      roadmapMesoOps.getByBeltAndStripe(beltColor, stripeCount)
    ]);

    return { macro, meso };
  } catch (error) {
    console.error('Failed to get curriculum by belt and stripe:', error);
    return { macro: [], meso: [] };
  }
}

/**
 * Search meso curriculum by technique
 */
export async function searchMesoCurriculumByTechnique(
  techniqueName: string
): Promise<RoadmapMesoOffline[]> {
  try {
    return await roadmapMesoOps.searchByTechnique(techniqueName);
  } catch (error) {
    console.error('Failed to search meso curriculum by technique:', error);
    return [];
  }
}

/**
 * Get active curriculum items only
 */
export async function getActiveCurriculum(): Promise<{
  macro: RoadmapMacroOffline[];
  meso: RoadmapMesoOffline[];
}> {
  try {
    const [macro, meso] = await Promise.all([
      roadmapMacroOps.getActive(),
      roadmapMesoOps.getActive()
    ]);

    return { macro, meso };
  } catch (error) {
    console.error('Failed to get active curriculum:', error);
    return { macro: [], meso: [] };
  }
}

/**
 * Create new macro curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function createMacroCurriculumOffline(
  curriculum: Omit<RoadmapMacroOffline, 'id' | 'created_at' | 'updated_at'>
): Promise<RoadmapMacroOffline> {
  try {
    return await roadmapMacroOps.create(curriculum);
  } catch (error) {
    console.error('Failed to create macro curriculum:', error);
    throw error;
  }
}

/**
 * Create new meso curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function createMesoCurriculumOffline(
  curriculum: Omit<RoadmapMesoOffline, 'id' | 'created_at' | 'updated_at'>
): Promise<RoadmapMesoOffline> {
  try {
    return await roadmapMesoOps.create(curriculum);
  } catch (error) {
    console.error('Failed to create meso curriculum:', error);
    throw error;
  }
}

/**
 * Update macro curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function updateMacroCurriculumOffline(
  id: string,
  updates: Partial<RoadmapMacroOffline>
): Promise<RoadmapMacroOffline | null> {
  try {
    return await roadmapMacroOps.update(id, updates);
  } catch (error) {
    console.error('Failed to update macro curriculum:', error);
    throw error;
  }
}

/**
 * Update meso curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function updateMesoCurriculumOffline(
  id: string,
  updates: Partial<RoadmapMesoOffline>
): Promise<RoadmapMesoOffline | null> {
  try {
    return await roadmapMesoOps.update(id, updates);
  } catch (error) {
    console.error('Failed to update meso curriculum:', error);
    throw error;
  }
}

/**
 * Delete macro curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function deleteMacroCurriculumOffline(id: string): Promise<void> {
  try {
    await roadmapMacroOps.delete(id);
  } catch (error) {
    console.error('Failed to delete macro curriculum:', error);
    throw error;
  }
}

/**
 * Delete meso curriculum item (admin only)
 * This will be queued for sync when online
 */
export async function deleteMesoCurriculumOffline(id: string): Promise<void> {
  try {
    await roadmapMesoOps.delete(id);
  } catch (error) {
    console.error('Failed to delete meso curriculum:', error);
    throw error;
  }
}

/**
 * Get curriculum statistics
 */
export async function getCurriculumStats(): Promise<{
  totalMacro: number;
  totalMeso: number;
  activeMacro: number;
  activeMeso: number;
  beltBreakdown: Record<string, { macro: number; meso: number }>;
}> {
  try {
    const [allMacro, allMeso, activeMacro, activeMeso] = await Promise.all([
      roadmapMacroOps.getAll(),
      roadmapMesoOps.getAll(),
      roadmapMacroOps.getActive(),
      roadmapMesoOps.getActive()
    ]);

    // Calculate belt breakdown
    const beltBreakdown: Record<string, { macro: number; meso: number }> = {};
    
    allMacro.forEach(item => {
      if (!beltBreakdown[item.belt_color]) {
        beltBreakdown[item.belt_color] = { macro: 0, meso: 0 };
      }
      beltBreakdown[item.belt_color].macro++;
    });

    allMeso.forEach(item => {
      if (!beltBreakdown[item.belt_color]) {
        beltBreakdown[item.belt_color] = { macro: 0, meso: 0 };
      }
      beltBreakdown[item.belt_color].meso++;
    });

    return {
      totalMacro: allMacro.length,
      totalMeso: allMeso.length,
      activeMacro: activeMacro.length,
      activeMeso: activeMeso.length,
      beltBreakdown
    };
  } catch (error) {
    console.error('Failed to get curriculum stats:', error);
    return {
      totalMacro: 0,
      totalMeso: 0,
      activeMacro: 0,
      activeMeso: 0,
      beltBreakdown: {}
    };
  }
}
